import {
  PolicyConditionField,
  PolicyConditionLinesNode,
  PolicyExclusiveChooser,
} from "../types/policyForm";

export function makeDefaultConditionLineItem(
  field: PolicyConditionField,
): Record<string, unknown> {
  return {
    template_key: field.templateKey ?? "",
    title: field.key,
    operator: field.defaultOperator?.symbol ?? "",
    value_type: field.apiValueType ?? "",
    value: field.apiValueType === "multi_select" ? [] : "",
  };
}

function resolveDefaultFieldKey(
  defaultConditions: PolicyConditionField[],
  fieldKeys: string[],
): string {
  const fieldKeySet = new Set(fieldKeys);

  for (const field of defaultConditions) {
    if (field.autoRender && fieldKeySet.has(field.autoRender)) {
      return field.autoRender;
    }
  }

  return fieldKeys[0];
}

export function buildExclusiveChoosers(
  defaultConditions: PolicyConditionField[],
): PolicyExclusiveChooser[] {
  const defaultKeySet = new Set(defaultConditions.map((field) => field.key));
  const processed = new Set<string>();
  const choosers: PolicyExclusiveChooser[] = [];

  for (const field of defaultConditions) {
    if (processed.has(field.key)) {
      continue;
    }

    const peers = (field.substituteKeys ?? []).filter((key) =>
      defaultKeySet.has(key),
    );
    if (peers.length === 0) {
      continue;
    }

    const groupKeys = new Set([field.key, ...peers]);
    const fieldKeys = defaultConditions
      .map((conditionField) => conditionField.key)
      .filter((key) => groupKeys.has(key));

    if (fieldKeys.length < 2) {
      continue;
    }

    fieldKeys.forEach((key) => processed.add(key));
    choosers.push({
      fieldKeys,
      defaultFieldKey: resolveDefaultFieldKey(defaultConditions, fieldKeys),
    });
  }

  return choosers;
}

export function getExclusiveFieldKeysSet(
  choosers: PolicyExclusiveChooser[],
): Set<string> {
  const keys = new Set<string>();
  choosers.forEach((chooser) => {
    chooser.fieldKeys.forEach((key) => keys.add(key));
  });
  return keys;
}

export function isLockedExclusiveChooser(
  chooser: PolicyExclusiveChooser,
  defaultConditions: PolicyConditionField[],
): boolean {
  const defaultKeySet = new Set(defaultConditions.map((field) => field.key));
  return chooser.fieldKeys.some((key) => defaultKeySet.has(key));
}

export function getLockedDefaultFieldKeys(
  defaultConditions: PolicyConditionField[],
  exclusiveChoosers: PolicyExclusiveChooser[],
): string[] {
  const exclusiveKeys = getExclusiveFieldKeysSet(exclusiveChoosers);
  return defaultConditions
    .filter((field) => !exclusiveKeys.has(field.key))
    .map((field) => field.key);
}

export function getMinLockedConditionRows(
  node: PolicyConditionLinesNode,
): number {
  let count = getLockedDefaultFieldKeys(
    node.defaultConditions,
    node.exclusiveChoosers,
  ).length;

  node.exclusiveChoosers.forEach((chooser) => {
    if (isLockedExclusiveChooser(chooser, node.defaultConditions)) {
      count += 1;
    }
  });

  return count;
}

export function isLockedConditionFieldKey(
  node: PolicyConditionLinesNode,
  fieldKey: string,
): boolean {
  const field = node.defaultConditions.find((item) => item.key === fieldKey);
  if (!field) {
    return false;
  }

  const chooser = node.exclusiveChoosers.find((item) =>
    item.fieldKeys.includes(fieldKey),
  );
  if (chooser) {
    return isLockedExclusiveChooser(chooser, node.defaultConditions);
  }

  return true;
}

export function isTemplateConditionLineTitle(
  node: PolicyConditionLinesNode,
  title: string,
): boolean {
  if (!title) {
    return false;
  }

  const exclusiveKeys = getExclusiveFieldKeysSet(node.exclusiveChoosers);
  if (exclusiveKeys.has(title)) {
    return true;
  }

  return node.defaultConditions.some((field) => field.key === title);
}

export function isLockedConditionLineTitle(
  node: PolicyConditionLinesNode,
  title: string,
): boolean {
  if (!title) {
    return false;
  }

  return isLockedConditionFieldKey(node, title);
}

export function getAddableConditionFields(
  node: PolicyConditionLinesNode,
): PolicyConditionField[] {
  const exclusiveKeys = getExclusiveFieldKeysSet(node.exclusiveChoosers);
  return node.optionalConditions.filter(
    (field) => !exclusiveKeys.has(field.key),
  );
}

export function getAddableExclusiveChoosers(
  node: PolicyConditionLinesNode,
  usedFieldKeys: Set<string>,
): PolicyExclusiveChooser[] {
  return node.exclusiveChoosers.filter(
    (chooser) =>
      !isLockedExclusiveChooser(chooser, node.defaultConditions) &&
      !chooser.fieldKeys.some((key) => usedFieldKeys.has(key)),
  );
}

export function getExclusiveChooserMenuLabel(
  node: PolicyConditionLinesNode,
  chooser: PolicyExclusiveChooser,
): string {
  const labels = chooser.fieldKeys
    .map(
      (key) => node.defaultConditions.find((field) => field.key === key)?.label,
    )
    .filter((label): label is string => Boolean(label));
  return labels.join(" / ");
}

export function resolveExclusiveChooserDefaultField(
  node: PolicyConditionLinesNode,
  chooser: PolicyExclusiveChooser,
): PolicyConditionField | undefined {
  return (
    node.defaultConditions.find(
      (field) => field.key === chooser.defaultFieldKey,
    ) ??
    node.defaultConditions.find((field) =>
      chooser.fieldKeys.includes(field.key),
    )
  );
}

function seedExclusiveChooserLine(
  node: PolicyConditionLinesNode,
  chooser: PolicyExclusiveChooser,
): Record<string, unknown> | null {
  const defaultField =
    node.defaultConditions.find(
      (field) => field.key === chooser.defaultFieldKey,
    ) ??
    node.defaultConditions.find((field) =>
      chooser.fieldKeys.includes(field.key),
    );
  return defaultField ? makeDefaultConditionLineItem(defaultField) : null;
}

export function buildStructuralConditionLineItems(
  node: PolicyConditionLinesNode,
  existingByTitle?: Map<string, Record<string, unknown>>,
): Record<string, unknown>[] {
  const exclusiveKeys = getExclusiveFieldKeysSet(node.exclusiveChoosers);
  const nonExclusiveDefaults = node.defaultConditions.filter(
    (field) => !exclusiveKeys.has(field.key),
  );
  const structural: Record<string, unknown>[] = [];

  for (const field of nonExclusiveDefaults) {
    const existing = existingByTitle?.get(field.key);
    if (existing) {
      structural.push(existing);
      continue;
    }
    structural.push(makeDefaultConditionLineItem(field));
  }

  for (const chooser of node.exclusiveChoosers) {
    const existing = chooser.fieldKeys
      .map((key) => existingByTitle?.get(key))
      .find((row) => row);
    if (existing) {
      structural.push(existing);
      continue;
    }
    const seeded = seedExclusiveChooserLine(node, chooser);
    if (seeded) {
      structural.push(seeded);
    }
  }

  return structural;
}

export function collectExtraConditionLineRows(
  node: PolicyConditionLinesNode,
  rows: Record<string, unknown>[],
  structuralRows: Record<string, unknown>[],
): Record<string, unknown>[] {
  const exclusiveKeys = getExclusiveFieldKeysSet(node.exclusiveChoosers);
  const structuralTitleSet = new Set(
    structuralRows.map((row) => String(row.title ?? "")),
  );
  const defaultKeySet = new Set(
    node.defaultConditions.map((field) => field.key),
  );

  return rows.filter((row) => {
    const title = String(row.title ?? "");
    if (!title) {
      return false;
    }
    if (exclusiveKeys.has(title) && !structuralTitleSet.has(title)) {
      return false;
    }

    if (defaultKeySet.has(title)) {
      return !structuralTitleSet.has(title);
    }

    return !defaultKeySet.has(title);
  });
}
